import { useMemo } from 'react';
import { useMyTodoLists, useSharedTodoLists } from './useTodoLists';
import type { TodoItem, TodoList } from '@/types/todolist';
import { isOverdue, isDueToday } from '@/types/todolist';
import type { Timestamp } from 'firebase/firestore';

// Extended task type with list context
export interface AggregatedTask extends TodoItem {
  listId: string;
  listName: string;
  listOwnerId: string;
  isSharedList: boolean;
}

export type TaskFilter = 'today' | 'upcoming' | 'overdue' | 'all';

/**
 * Check if a due date falls within the next N days
 */
const isWithinDays = (dueDate: Timestamp | null, days: number): boolean => {
  if (!dueDate) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = dueDate.toDate();
  due.setHours(0, 0, 0, 0);
  const future = new Date(now);
  future.setDate(future.getDate() + days);
  return due >= now && due <= future;
};

/**
 * Get the day key for grouping (e.g., "2024-01-15")
 */
export const getDayKey = (dueDate: Timestamp): string => {
  const date = dueDate.toDate();
  return date.toISOString().split('T')[0];
};

/**
 * Format a date for display as a day header
 */
export const formatDayHeader = (dateKey: string): string => {
  const date = new Date(dateKey + 'T00:00:00');
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === now.toDateString()) {
    return 'Today';
  }
  if (date.toDateString() === tomorrow.toDateString()) {
    return 'Tomorrow';
  }

  return date.toLocaleDateString('en-NZ', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Hook to get aggregated tasks across all todo lists
 */
export const useAggregatedTasks = (filter: TaskFilter) => {
  const { data: ownedLists, isLoading: loadingOwned } = useMyTodoLists();
  const { data: sharedLists, isLoading: loadingShared } = useSharedTodoLists();

  const loading = loadingOwned || loadingShared;

  const result = useMemo(() => {
    const allLists: Array<{ list: TodoList; isShared: boolean }> = [
      ...(ownedLists || []).map((list) => ({ list, isShared: false })),
      ...(sharedLists || []).map((list) => ({ list, isShared: true })),
    ];

    const tasks: AggregatedTask[] = [];

    for (const { list, isShared } of allLists) {
      for (const item of list.items) {
        // Skip completed items for today/upcoming/overdue views
        if (filter !== 'all' && item.completed) continue;

        const aggregatedItem: AggregatedTask = {
          ...item,
          listId: list.id,
          listName: list.name,
          listOwnerId: list.ownerId,
          isSharedList: isShared,
        };

        // Apply filter
        switch (filter) {
          case 'today':
            if (isDueToday(item.dueDate) || isOverdue(item.dueDate)) {
              tasks.push(aggregatedItem);
            }
            break;
          case 'upcoming':
            // Next 7 days, including today
            if (isWithinDays(item.dueDate, 7)) {
              tasks.push(aggregatedItem);
            }
            break;
          case 'overdue':
            if (isOverdue(item.dueDate)) {
              tasks.push(aggregatedItem);
            }
            break;
          case 'all':
          default:
            tasks.push(aggregatedItem);
            break;
        }
      }
    }

    // Sort tasks
    tasks.sort((a, b) => {
      // First, sort by due date (null dates go to end)
      if (a.dueDate && b.dueDate) {
        const diff = a.dueDate.seconds - b.dueDate.seconds;
        if (diff !== 0) return diff;
      } else if (a.dueDate && !b.dueDate) {
        return -1;
      } else if (!a.dueDate && b.dueDate) {
        return 1;
      }

      // Then by priority (high > medium > low > null)
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      const aPriority = a.priority ? priorityOrder[a.priority] : 3;
      const bPriority = b.priority ? priorityOrder[b.priority] : 3;
      if (aPriority !== bPriority) return aPriority - bPriority;

      // Finally by title
      return a.title.localeCompare(b.title);
    });

    return tasks;
  }, [ownedLists, sharedLists, filter]);

  // Group tasks by day for upcoming view
  const groupedByDay = useMemo(() => {
    if (filter !== 'upcoming') return null;

    const groups: Map<string, AggregatedTask[]> = new Map();

    // Pre-populate next 7 days
    const now = new Date();
    for (let i = 0; i < 7; i++) {
      const date = new Date(now);
      date.setDate(date.getDate() + i);
      const key = date.toISOString().split('T')[0];
      groups.set(key, []);
    }

    // Populate with tasks
    for (const task of result) {
      if (task.dueDate) {
        const key = getDayKey(task.dueDate);
        const existing = groups.get(key);
        if (existing) {
          existing.push(task);
        }
      }
    }

    return groups;
  }, [result, filter]);

  // Separate today's tasks into overdue and due today
  const todayGroups = useMemo(() => {
    if (filter !== 'today') return null;

    const overdue: AggregatedTask[] = [];
    const dueToday: AggregatedTask[] = [];
    const noDueDate: AggregatedTask[] = [];

    for (const task of result) {
      if (isOverdue(task.dueDate)) {
        overdue.push(task);
      } else if (isDueToday(task.dueDate)) {
        dueToday.push(task);
      } else {
        noDueDate.push(task);
      }
    }

    return { overdue, dueToday, noDueDate };
  }, [result, filter]);

  // Count helpers
  const todayCount = useMemo(() => {
    if (!ownedLists && !sharedLists) return 0;

    let count = 0;
    const allLists = [...(ownedLists || []), ...(sharedLists || [])];

    for (const list of allLists) {
      for (const item of list.items) {
        if (!item.completed && (isDueToday(item.dueDate) || isOverdue(item.dueDate))) {
          count++;
        }
      }
    }

    return count;
  }, [ownedLists, sharedLists]);

  const upcomingCount = useMemo(() => {
    if (!ownedLists && !sharedLists) return 0;

    let count = 0;
    const allLists = [...(ownedLists || []), ...(sharedLists || [])];

    for (const list of allLists) {
      for (const item of list.items) {
        if (!item.completed && isWithinDays(item.dueDate, 7)) {
          count++;
        }
      }
    }

    return count;
  }, [ownedLists, sharedLists]);

  return {
    tasks: result,
    loading,
    groupedByDay,
    todayGroups,
    todayCount,
    upcomingCount,
  };
};
